'use strict';

/** @typedef {import('../pipelineStatusTypes').PipelineStatusStage} PipelineStatusStage */

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { formatNodeDurationLabel } from '../pipelineDuration';
import { pipelineStatusNodeAccent } from './pipelineStatusGraphEdge';
import { LAYOUT_NODE_WIDTH } from './pipelineStatusGraphLayout';

const HANDLE_STYLE = { top: 36, width: 8, height: 8, background: '#94a3b8', border: 'none' };
const SLOT_SX = { display: 'block', mt: 0.5, lineHeight: 1.35, minHeight: '1.35em' };

/** @param {string} status */
function chipColor(status) {
	switch (status) {
		case 'success':
		case 'skipped':
			return 'success';
		case 'failed':
			return 'error';
		case 'running':
			return 'info';
		default:
			return 'default';
	}
}

/**
 * Custom React Flow node: MUI Card, production-style (no tutorial chrome).
 * Left/right handles match left-to-right flow; handles are omitted when the node has no incident edge on that side.
 * @param {{ id: string, data: { stage: PipelineStatusStage, graphHints?: { hasIncoming?: boolean, hasOutgoing?: boolean } } }} props
 */
function PipelineStatusStageNodeInner({ data }) {
	const s = data?.stage;
	const hints = data?.graphHints;
	const showTarget = hints != null ? Boolean(hints.hasIncoming) : true;
	const showSource = hints != null ? Boolean(hints.hasOutgoing) : true;
	if (!s) return null;

	const workflow =
		s.workflow != null && typeof s.workflow === 'string' && s.workflow.trim() ? s.workflow.trim() : null;
	const producer =
		s.service != null && typeof s.service === 'string' && s.service.trim() ? s.service.trim() : null;
	const metaLine = [workflow, producer].filter(Boolean).join(' · ');
	const durationLabel = formatNodeDurationLabel({
		startedAt: s.startedAt,
		completedAt: s.completedAt,
		status: s.status,
	});

	const highlighted = Boolean(hints?.highlighted);

	return (
		<Card
			elevation={0}
			sx={{
				boxSizing: 'border-box',
				width: LAYOUT_NODE_WIDTH,
				minWidth: LAYOUT_NODE_WIDTH,
				maxWidth: LAYOUT_NODE_WIDTH,
				borderRadius: 1,
				border: 1,
				borderColor: highlighted ? 'primary.main' : 'divider',
				borderLeftWidth: 4,
				borderLeftColor: pipelineStatusNodeAccent(s.status),
				bgcolor: 'background.paper',
				boxShadow: highlighted
					? '0 0 0 3px rgba(21, 101, 192, 0.15)'
					: '0 1px 2px rgba(15, 23, 42, 0.06)',
			}}
		>
			{showTarget ? (
				<Handle type="target" position={Position.Left} isConnectable={false} style={HANDLE_STYLE} />
			) : null}
			<CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
					<Chip size="small" label={s.status} color={chipColor(s.status)} variant="outlined" sx={{ height: 22 }} />
				</Box>
				<Typography
					variant="subtitle2"
					sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
				>
					{s.description || s.stage}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={SLOT_SX}>
					{metaLine || '\u00a0'}
				</Typography>
				{typeof s.iterationTotalCount === 'number' ? (
					<Typography variant="caption" color="text.secondary" sx={SLOT_SX}>
						{typeof s.iterationCompletedCount === 'number' ? s.iterationCompletedCount : 0} / {s.iterationTotalCount}{' '}
						completed
					</Typography>
				) : null}
				<Typography variant="caption" color="text.secondary" sx={SLOT_SX}>
					{durationLabel || '\u00a0'}
				</Typography>
			</CardContent>
			{showSource ? (
				<Handle type="source" position={Position.Right} isConnectable={false} style={HANDLE_STYLE} />
			) : null}
		</Card>
	);
}

export const PipelineStatusStageNode = memo(PipelineStatusStageNodeInner);
